import { useTranslation } from '@thoughtspot/i18n';
import {
    Accordion,
    AccordionVariant,
} from '@thoughtspot/radiant-react/widgets/accordion';
import { Vertical } from '@thoughtspot/radiant-react/widgets/layout';
import { Popover } from '@thoughtspot/radiant-react/widgets/popover';
import cx from 'classnames';
import React from 'react';
import { getNestedAccordionDataTestId } from '../trial-info.util';
import { ChecklistAccordionHeader } from './checklist-accordion-header';
import styles from './checklist-modal.module.scss';
import {
    ChecklistItem,
    ChecklistModalProps,
    ChecklistTaskType,
} from './checklist-modal.types';
import { ChecklistTask } from './checklist-task';

export const ChecklistModal: React.FC<ChecklistModalProps> = ({
    checklistItems,
    checklistOpen,
    setChecklistOpen,
    checklistButtonRef,
    checkedItems,
    onChecklistTaskButtonClick,
    headerExpandedItemsIndexes,
    handleSectionHeaderClick,
    taskExpandedState,
    handleTaskHeaderClick,
    onCloseChecklist,
    lastCompletedTaskId,
}) => {
    const { t } = useTranslation();
    const renderTaskContent = (content: ChecklistTaskType) => {
        return (
            <Vertical className={styles.checklistItemContent}>
                <ChecklistTask
                    content={content}
                    isSubtask={false}
                    checkedItems={checkedItems}
                    onChecklistTaskButtonClick={onChecklistTaskButtonClick}
                    key={content?.id}
                    lastCompletedTaskId={lastCompletedTaskId}
                />
                {content?.sub_tasks?.length > 0 &&
                    content.sub_tasks.map((subtask: ChecklistTaskType) => (
                        <ChecklistTask
                            content={subtask}
                            isSubtask
                            checkedItems={checkedItems}
                            onChecklistTaskButtonClick={
                                onChecklistTaskButtonClick
                            }
                            key={subtask?.id}
                            lastCompletedTaskId={lastCompletedTaskId}
                        />
                    ))}
            </Vertical>
        );
    };

    const renderTaskAccordion = (content: ChecklistTaskType) => {
        return (
            <Accordion.Item
                key={content.id}
                header={
                    <ChecklistAccordionHeader
                        title={t(content.name)}
                        checkboxId={content.id}
                        checkedItems={checkedItems}
                        lastCompletedTaskId={lastCompletedTaskId}
                    />
                }
                data-testid={getNestedAccordionDataTestId(content.id)}
                className={styles.nestedChecklistAccordionItem}
            >
                {renderTaskContent(content)}
            </Accordion.Item>
        );
    };

    const renderSectionAccordion = (item: ChecklistItem) => {
        const sectionTaskIds = item.tasks.flatMap((task: ChecklistTaskType) => [
            task.id,
            ...(task.sub_tasks?.map(
                (subtask: ChecklistTaskType) => subtask.id,
            ) || []),
        ]);

        const firstUncheckedIndex = item.tasks.findIndex(
            (task: ChecklistTaskType) => !checkedItems.has(task.id),
        );
        const expandedIndexes = taskExpandedState[item.id] || [
            firstUncheckedIndex ?? 0,
        ];

        return (
            <Accordion.Item
                key={item.name}
                header={
                    <ChecklistAccordionHeader
                        title={t(item.name)}
                        checkboxId={item.id}
                        checkedItems={checkedItems}
                        isSectionHeader
                        sectionTaskIds={sectionTaskIds}
                    />
                }
            >
                <Accordion
                    key={item.id}
                    expandedItemsIndexes={expandedIndexes}
                    expandMultipleItems
                    useContainedHeight={false}
                    noPadding
                    variant={AccordionVariant.SIDE_NAV}
                    className={styles.nestedChecklistAccordion}
                    onAccordionChange={(expandedIndexes: number[]) =>
                        handleTaskHeaderClick(item.id, expandedIndexes)
                    }
                >
                    {item.tasks.map((content: ChecklistTaskType) =>
                        renderTaskAccordion(content),
                    )}
                </Accordion>
            </Accordion.Item>
        );
    };

    const renderAccordionChecklist = () => {
        return (
            <Accordion
                expandMultipleItems
                expandedItemsIndexes={headerExpandedItemsIndexes}
                useContainedHeight
                noPadding
                variant={AccordionVariant.MULTI_LEVEL}
                className={cx(
                    styles.checklistAccordion,
                    styles.marginChecklistAccordion,
                )}
                onAccordionChange={handleSectionHeaderClick}
            >
                {checklistItems?.map((item: ChecklistItem) =>
                    renderSectionAccordion(item),
                )}
            </Accordion>
        );
    };
    return (
        <>
            {checklistItems?.length > 0 && (
                <Popover
                    content={renderAccordionChecklist()}
                    align="bottom"
                    contentWidth="targetWidth"
                    appendToBody
                    showAlignArrow
                    targetRef={checklistButtonRef}
                    isVisible={checklistOpen}
                    onClickOutside={onCloseChecklist}
                    className={styles.checklistPopover}
                    leftOffset={-116}
                    bottomOffset={2}
                />
            )}
        </>
    );
};
